import "./timeline-resources.css";

import {
  AssetLinkingInput,
  ExtensionMessageContext,
  ExtensionWidgetContext,
  GenerateTripsForShipmentsMutation,
  GetAllDriverShiftsQuery,
  GetAllMaintenanceTasksQuery,
  GetAssignmentAssetsQuery,
  GetBusinessEntityListQuery,
  GetDriverShiftAssignmentsQuery,
  GetTodayTripsQuery,
  GetShipmentListForPlanningQuery,
  ShipmentSortCriteria,
  Status,
} from "../../../graphql/generated";
import { Badge, Box, Button, Fab, Stack } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  AutoAwesome,
  Close,
  DragHandle,
  FilterAlt,
  Undo,
  Warning,
} from "@mui/icons-material";
import { AssetFiltersValue } from "./AssetFilters";
import { LoadFiltersValue } from "./LoadFilters";
import PlanningGrid from "./PlanningGrid";
import PlanningCalendar from "./PlanningCalendar";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { debounce, isEmpty, omitBy } from "lodash";
import AssetFiltersModal from "./AssetFiltersModal";
import { useTranslation } from "react-i18next";
import AutoPlanModalContainer from "./AutoPlanModal";
import ExtensionsMessagesContainer from "../../extensions/ExtensionsMessages";
import AssetLinkingFormModalContainer from "../../asset-management/AssetLinkingFormModal";
import ExtensionsWidgetsContainer from "../../extensions/ExtensionsWidgets";
import DriverShiftAssignmentFormModalContainer from "../../driver-shifts/DriverShiftAssignmentFormModal";
import LocaleProvider from "../../../providers/LocaleProvider";
import { zonedTimeToUtc } from "date-fns-tz";
import { endOfDay, startOfDay } from "date-fns";
import { AssetConflict } from "./assetValidation";
import { testIds } from "./PlanningTool.testIds";

export type Assignment = {
  shipmentId: string;
  driverId: string;
  startTime: Date;
};

type PlanningShipmentEvent = NonNullable<
  GetTodayTripsQuery["trips"]["data"][0]["shipments"][0]["events"]
>[number] & {
  tripShipmentLocation?: string | null;
};

export type AugmentedShipment =
  GetShipmentListForPlanningQuery["shipments"]["data"][0] & {
    originState: string | null;
    destinationState: string | null;
    originCity: string | null;
    destinationCity: string | null;
    earliestPickup: Date;
    latestPickup: Date;
    earliestDropoff: Date;
    latestDropoff: Date;
    otherShipmentsInTrip: AugmentedShipment[];
    previousPickupTime?: Date | null;
    events?: PlanningShipmentEvent[] | null;
  };

export type AugmentedDriver = GetAssignmentAssetsQuery["drivers"]["data"][0] & {
  associatedTractor: GetAssignmentAssetsQuery["assetLinkings"]["data"][0]["tractor"];
  associatedTrailer: GetAssignmentAssetsQuery["assetLinkings"]["data"][0]["trailer"];
  associatedAdditionalTrailers: GetAssignmentAssetsQuery["assetLinkings"]["data"][0]["additionalTrailers"];
  assetLinking?: GetAssignmentAssetsQuery["assetLinkings"]["data"][0];
  driverShiftAssignment?: GetDriverShiftAssignmentsQuery["driverShiftAssignments"]["data"][0];
};

export type PlanningToolProps = {
  drivers: AugmentedDriver[];
  tractors: GetAssignmentAssetsQuery["tractors"]["data"];
  trailers: GetAssignmentAssetsQuery["trailers"]["data"];
  shipments: AugmentedShipment[];
  filteredShipments: AugmentedShipment[];
  driverShifts: GetAllDriverShiftsQuery["driverShifts"]["data"];
  maintenanceTasks: GetAllMaintenanceTasksQuery["maintenanceTasks"]["data"];
  domiciles: GetBusinessEntityListQuery["businessEntities"]["data"];
  assetFilters: AssetFiltersValue;
  loadFilters: LoadFiltersValue;
  canSave?: boolean;
  canPlan?: boolean;
  canRevert?: boolean;
  showDriveTimes?: boolean;
  onPeriodChange: (period: { start: Date; end: Date }) => void;
  onAssetSearchChange: (search: string) => void;
  onAssetFiltersChange: (filters: AssetFiltersValue) => void;
  onLoadFiltersChange: (filters: LoadFiltersValue) => void;
  onLoadSearchChange: (search: string) => void;
  onSortChange: (sorCriteria: ShipmentSortCriteria) => void;
  onSortOrderDescChange: (sortDesc: boolean) => void;
  onShipmentsChange: (
    shipments: AugmentedShipment[],
    changedDriver?: string
  ) => void;
  onShipmentsCombine: (shipments: AugmentedShipment[]) => void;
  onShipmentsUncombine: (shipments: AugmentedShipment[]) => void;
  onFilteredShipmentsChange: (shipments: AugmentedShipment[]) => void;
  onRefresh: (shipmentIds: string[]) => Promise<void>;
  onRefreshAll: () => Promise<void>;
  onSplitApplied: (parentId: string, childIds: string[]) => Promise<void>;
  onAssetsRefresh: () => Promise<void>;
  onAssetLinkingChange: (
    driverId: string,
    assetLinkingInput: AssetLinkingInput,
    assetLinkingId?: string
  ) => void;
  validateAssetAssignment?: (
    assetLinkingInput: AssetLinkingInput,
    currentDriverId: string
  ) => AssetConflict[];
  onSubmit: () => Promise<void>;
  onPlan: () => Promise<void>;
  onRevert: () => Promise<void>;
  onAutoPlanSubmit: (
    result: GenerateTripsForShipmentsMutation["generateTripsForShipments"]
  ) => void;
  isAutoPlanModalOpen: boolean;
};

const PlanningTool = ({
  onPeriodChange,
  onSortChange,
  onSortOrderDescChange,
  onRefresh,
  isAutoPlanModalOpen,
  ...props
}: PlanningToolProps) => {
  const { t } = useTranslation(["planning", "common"]);
  const [assetFilterDialogOpen, setAssetFilterDialogOpen] = useState(false);

  const [calendarPanelSize, setCalendarPanelSize] = useState(0);
  const setCalendarPanelSizeDebounced = debounce(setCalendarPanelSize, 300);
  const [currentPeriod, setCurrentPeriod] =
    useState<
      | {
          start: Date;
          end: Date;
        }
    >({
      start: startOfDay(new Date()),
      end: endOfDay(new Date()),
    });

  const [selectedShipments, setSelectedShipments] = useState<
    AugmentedShipment[]
  >([]);

  const [autoplanModalOpen, setAutoplanModalOpen] =
    useState(isAutoPlanModalOpen);

  const [assetLinkingOpen, setAssetLinkingOpen] = useState(false);
  const [assetLinkingDriverId, setAssetLinkingDriverId] =
    useState<string | null>(null);
  const [initialAssetLinking, setInitialAssetLinking] =
    useState<GetAssignmentAssetsQuery["assetLinkings"]["data"][0] | null>(null);

  const [driverAssignmentOpen, setDriverAssignmentOpen] = useState(false);
  const [driverAssignmentDriverId, setDriverAssignmentDriverId] =
    useState<string | null>(null);
  const [driverAssignmentId, setDriverAssignmentId] =
    useState<string | null>(null);

  const driverAssignmentDriver = useMemo(
    () =>
      props.drivers.find((driver) => driver._id === driverAssignmentDriverId),
    [props.drivers, driverAssignmentDriverId]
  );
  const driverAssignmentTimezone =
    driverAssignmentDriver?.domicileEntity?.addressTimezone ||
    LocaleProvider.getTimezone();

  const handleRefresh = useCallback(
    async (shipmentIds: string[]) => {
      await onRefresh(shipmentIds);
    },
    [onRefresh]
  );

  const handleAssetLinkingOpen = (driverId: string) => {
    const driver = props.drivers.find((driver) => driver._id === driverId);
    if (!driver) {
      return;
    }
    setInitialAssetLinking(driver.assetLinking || null);
    setAssetLinkingDriverId(driverId);
    setAssetLinkingOpen(true);
  };

  const handleDriverShiftAssignmentOpen = (driverId: string) => {
    const driver = props.drivers.find((driver) => driver._id === driverId);
    if (!driver) {
      return;
    }
    setDriverAssignmentId(driver.driverShiftAssignment?._id || null);
    setDriverAssignmentDriverId(driverId);
    setDriverAssignmentOpen(true);
  };

  const headerIndentTemplate = useCallback(() => {
    return (
      <Stack
        direction="row"
        alignItems="start"
        spacing={1}
        sx={{
          p: 1,
        }}
      >
        <Badge
          badgeContent={Object.keys(omitBy(props.assetFilters, isEmpty)).length}
          color="primary"
        >
          <Button data-testid={testIds.planningToolFilterAssets}
            variant="outlined"
            size="large"
            onClick={() => {
              setAssetFilterDialogOpen(true);
            }}
          >
            <FilterAlt />
          </Button>
        </Badge>
      </Stack>
    );
  }, [props.assetFilters]);

  const onShipmentsSelect = useCallback((shipments: AugmentedShipment[]) => {
    setSelectedShipments(shipments);
  }, []);

  const unplannedShipments = useMemo(
    () =>
      props.shipments.filter(
        (shipment) => shipment.status === Status.Unplanned
      ),
    [props.shipments]
  );

  const [unplannedShipmentsToggled, setUnplannedShipmentsToggled] =
    useState(false);
  const [pendingSaveCount, setPendingSaveCount] = useState(0);

  return (
    <Box sx={{ height: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <Box>
          <AssetFiltersModal
            open={assetFilterDialogOpen}
            onClose={() => setAssetFilterDialogOpen(false)}
            filters={props.assetFilters}
            onChange={props.onAssetFiltersChange}
            domiciles={props.domiciles}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: "calc(100%)",
        }}
      >
        <PanelGroup
          direction="vertical"
          style={{ height: "100%", overflow: "visible" }}
          autoSaveId="PlanningTool::MainPanelGroup"
        >
          <Panel style={{ overflow: "auto" }} defaultSize={40} minSize={10}>
            <PlanningGrid
              {...props}
              filteredShipments={
                unplannedShipmentsToggled
                  ? unplannedShipments
                  : props.filteredShipments
              }
              onShipmentsSelect={onShipmentsSelect}
              onRefresh={handleRefresh}
            />
          </Panel>

          <PanelResizeHandle
            style={{
              height: 24,
              textAlign: "center",
            }}
          >
            <DragHandle />
          </PanelResizeHandle>
          <Panel
            defaultSize={60}
            minSize={10}
            onResize={(size) => {
              setCalendarPanelSizeDebounced(size);
            }}
            style={
              {
                // overflow: "visible",
              }
            }
          >
            <div className="schedule-container" style={{ height: "100%" }}>
              <PlanningCalendar
                {...props}
                onPeriodChange={(period) => {
                  onPeriodChange(period);
                  setCurrentPeriod(period);
                }}
                selectedShipmentId={
                  selectedShipments.length === 1 &&
                  props.filteredShipments.some(
                    (s) => s._id === selectedShipments[0]._id
                  )
                    ? selectedShipments[0]._id
                    : null
                }
                parentHeight={calendarPanelSize}
                headerIndentTemplate={headerIndentTemplate}
                onRefresh={handleRefresh}
                selectedDate={currentPeriod?.start}
                onAssetLinkingOpen={handleAssetLinkingOpen}
                onDriverShiftAssignmentOpen={handleDriverShiftAssignmentOpen}
                shipments={props.filteredShipments}
              />
            </div>
          </Panel>
        </PanelGroup>
      </Box>

      <AutoPlanModalContainer
        isOpen={autoplanModalOpen}
        onClose={() => setAutoplanModalOpen(false)}
        onPlanned={(result) => {
          props.onAutoPlanSubmit(result);
          setAutoplanModalOpen(false);
        }}
        currentPeriod={
          currentPeriod || {
            start: startOfDay(new Date()),
            end: endOfDay(new Date()),
          }
        }
      />

      {assetLinkingDriverId ? (
        <AssetLinkingFormModalContainer
          isOpen={assetLinkingOpen}
          initialAssetLinking={initialAssetLinking || undefined}
          selectedDriverId={assetLinkingDriverId || undefined}
          initialStartDate={currentPeriod?.start}
          initialEndDate={currentPeriod?.end}
          overrideCurrentAssignment
          deferSave={true}
          validateAssetAssignment={props.validateAssetAssignment}
          onAssetLinkingChange={(driverId, assetLinkingInput, assetLinkingId) => {
            props.onAssetLinkingChange(driverId, assetLinkingInput, assetLinkingId);
            setAssetLinkingOpen(false);
            setAssetLinkingDriverId(null);
            setInitialAssetLinking(null);
          }}
          onClose={() => {
            setAssetLinkingOpen(false);
            setAssetLinkingDriverId(null);
            setInitialAssetLinking(null);
          }}
        />
      ) : null}

      {driverAssignmentDriverId ? (
        <DriverShiftAssignmentFormModalContainer
          isOpen={driverAssignmentOpen}
          driverShiftAssignmentId={driverAssignmentId || undefined}
          selectedDriverId={driverAssignmentDriverId || undefined}
          initialStartDate={
            currentPeriod?.start
              ? zonedTimeToUtc(currentPeriod.start, driverAssignmentTimezone)
              : undefined
          }
          initialEndDate={
            currentPeriod?.end
              ? zonedTimeToUtc(currentPeriod.end, driverAssignmentTimezone)
              : undefined
          }
          onClose={() => {
            setDriverAssignmentOpen(false);
            setDriverAssignmentDriverId(null);
            setDriverAssignmentId(null);
          }}
          onSaved={() => {
            props.onAssetsRefresh();
          }}
        />
      ) : null}

      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: 16,
        }}
      >
        <ExtensionsMessagesContainer
          context={ExtensionMessageContext.PlanningTool}
          onActionWidgetClose={() => {
            props.onRefreshAll();
          }}
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          width: "auto",
          px: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <ExtensionsWidgetsContainer
          context={ExtensionWidgetContext.PlanningToolHeader}
        />
        <Button data-testid={testIds.planningToolAutoPlan}
          variant="contained"
          sx={{
            ml: 3,
            borderRadius: 16,
          }}
          color="secondary"
          size="small"
          onClick={async () => {
            setAutoplanModalOpen(true);
          }}
        >
          <AutoAwesome /> {t("autoPlanModal.title", "Auto Plan")}
        </Button>
      </Box>

      {unplannedShipments.length ? (
        <Fab data-testid={testIds.planningToolToggleUnplannedOrders}
          sx={{
            position: "absolute",
            bottom: 36,
            right: 140,
            width: "auto",
            py: 3,
            px: 3,
            borderRadius: 16,
          }}
          color="warning"
          variant="extended"
          onClick={async () => {
            setUnplannedShipmentsToggled(!unplannedShipmentsToggled);
          }}
        >
          {unplannedShipmentsToggled ? <Close /> : <Warning />}{" "}
          {t("unplannedOrders", "{{count}} Unplanned Orders", {
            count: unplannedShipments.length,
          })}
        </Fab>
      ) : null}

      <Box
        data-testid={testIds.planningToolActions}
        data-has-pending-changes={props.canSave ? "true" : "false"}
        data-save-state={pendingSaveCount > 0 ? "saving" : "idle"}
      >
        <Fab data-testid={testIds.planningToolPlan}
          sx={{
            position: "absolute",
            bottom: 32,
            left: 32,
            width: "auto",
            py: 3,
            px: 3,
            borderRadius: 16,
          }}
          color="secondary"
          disabled={!props.canPlan}
          onClick={async () => {
            await props.onPlan();
          }}
        >
          {t("planning:plan", "Plan")}
        </Fab>

        <Fab data-testid={testIds.planningToolRevert}
          sx={{
            position: "absolute",
            bottom: 32,
            left: 140,
            width: "auto",
            py: 3,
            px: 2,
            borderRadius: 16,
          }}
          color="error"
          disabled={!props.canRevert}
          onClick={async () => {
            await props.onRevert();
          }}
        >
          <Undo />
        </Fab>

        <Fab data-testid={testIds.planningToolSave}
          sx={{
            position: "absolute",
            bottom: 32,
            right: 32,
            width: "auto",
            py: 3,
            px: 3,
            borderRadius: 16,
          }}
          disabled={!props.canSave}
          color="primary"
          onClick={async () => {
            setPendingSaveCount((count) => count + 1);
            try {
              await props.onSubmit();
            } finally {
              setPendingSaveCount((count) => Math.max(0, count - 1));
            }
          }}
        >
          {t("common:save")}
        </Fab>
      </Box>
    </Box>
  );
};

export default PlanningTool;
